import { convertToPln, divideMinor, todayIso } from '@apka/core'
import type { Cadence, FxRate } from '@apka/core'
import { useLiveQuery } from 'dexie-react-hooks'
import { useMemo, useState } from 'react'
import { BladApi } from '../dane/api.js'
import { db } from '../dane/db.js'
import { usunPozycje } from '../dane/mutacje.js'
import type { Pozycja } from '../dane/typy.js'
import { uzyjDostawcow, uzyjKategorie, uzyjPozycje } from '../dane/widoki.js'
import { IkonaKosz, IkonaSzukaj } from '../ui/Ikony.js'
import { Potwierdzenie } from '../ui/Potwierdzenie.js'
import { SzkieletPozycji } from '../ui/Szkielet.js'
import { useToast } from '../ui/Toast.js'
import { waluta, zl } from '../ui/format.js'
import { FormularzPozycji } from './FormularzPozycji.js'
import './koszty.css'

const NAZWY_RYTMU: Record<Cadence, string> = {
  monthly: 'miesięcznie',
  yearly: 'rocznie',
  one_off: 'jednorazowo',
}

/**
 * Sprowadza koszt pozycji do wspólnej miary — złotówek na miesiąc — żeby dało
 * się porównać abonament za 12 EUR z domeną za 60 zł rocznie. Pozycje
 * jednorazowe nie mają miesięcznego odpowiednika.
 */
export function miesiecznieWZl(
  pozycja: Pozycja,
  kursy: readonly FxRate[],
  dzis: string,
): number | null {
  if (pozycja.cadence === 'one_off') return null
  const przeliczone = convertToPln(pozycja.amountMinor, pozycja.currency, dzis, kursy)
  if (przeliczone === null) return null
  return pozycja.cadence === 'yearly'
    ? divideMinor(przeliczone.amountPlnMinor, 12)
    : przeliczone.amountPlnMinor
}

export function Koszty() {
  const pozycje = uzyjPozycje()
  const kategorie = uzyjKategorie() ?? []
  const dostawcy = uzyjDostawcow() ?? []
  const kursy = useLiveQuery(() => db.fxRates.toArray(), []) ?? []
  const toast = useToast()

  const [szukane, ustawSzukane] = useState('')
  const [rytm, ustawRytm] = useState<'wszystkie' | Cadence>('wszystkie')
  const [kategoria, ustawKategorie] = useState('wszystkie')
  const [edytowana, ustawEdytowana] = useState<Pozycja | null>(null)
  const [doUsuniecia, ustawDoUsuniecia] = useState<Pozycja | null>(null)

  const dzis = todayIso()
  const nazwyKategorii = new Map(kategorie.map((wpis) => [wpis.id, wpis]))
  const nazwyDostawcow = useMemo(
    () => new Map(dostawcy.map((wpis) => [wpis.id, wpis.name])),
    [dostawcy],
  )

  const widoczne = useMemo(() => {
    const fraza = szukane.trim().toLowerCase()
    return (pozycje ?? []).filter((pozycja) => {
      if (rytm !== 'wszystkie' && pozycja.cadence !== rytm) return false
      if (kategoria !== 'wszystkie' && (pozycja.categoryId ?? '') !== kategoria) return false
      if (fraza === '') return true
      const dostawca = pozycja.vendorId === null ? '' : (nazwyDostawcow.get(pozycja.vendorId) ?? '')
      return pozycja.title.toLowerCase().includes(fraza) || dostawca.toLowerCase().includes(fraza)
    })
  }, [pozycje, szukane, rytm, kategoria, nazwyDostawcow])

  const sumaMiesieczna = widoczne.reduce(
    (suma, pozycja) => suma + (miesiecznieWZl(pozycja, kursy, dzis) ?? 0),
    0,
  )

  async function usun(pozycja: Pozycja) {
    try {
      await usunPozycje(pozycja.id)
      toast.pokaz('Usunięto pozycję')
    } catch (problem) {
      toast.pokazBlad(problem instanceof BladApi ? problem.message : 'Nie udało się usunąć')
    } finally {
      ustawDoUsuniecia(null)
    }
  }

  return (
    <>
      <header className="naglowek-ekranu">
        <div>
          <h1>Koszty</h1>
          <p>
            {widoczne.length} {widoczne.length === 1 ? 'pozycja' : 'pozycji'} · {zl(sumaMiesieczna)}{' '}
            miesięcznie
          </p>
        </div>
      </header>

      <div className="filtry">
        <div className="filtry__szukaj">
          <IkonaSzukaj width={18} height={18} />
          <input
            type="search"
            value={szukane}
            onChange={(zdarzenie) => ustawSzukane(zdarzenie.target.value)}
            placeholder="Szukaj po tytule lub dostawcy"
            aria-label="Szukaj pozycji"
          />
        </div>

        <div className="filtry__pigulki" role="group" aria-label="Filtr rytmu">
          {(
            [
              ['wszystkie', 'Wszystkie'],
              ['monthly', 'Miesięczne'],
              ['yearly', 'Roczne'],
              ['one_off', 'Jednorazowe'],
            ] as const
          ).map(([wartosc, etykieta]) => (
            <button
              key={wartosc}
              type="button"
              className={rytm === wartosc ? 'pigulka pigulka--wybrana' : 'pigulka'}
              aria-pressed={rytm === wartosc}
              onClick={() => ustawRytm(wartosc)}
            >
              {etykieta}
            </button>
          ))}
        </div>

        <select
          className="filtry__wybor"
          value={kategoria}
          onChange={(zdarzenie) => ustawKategorie(zdarzenie.target.value)}
          aria-label="Filtr kategorii"
        >
          <option value="wszystkie">Wszystkie kategorie</option>
          <option value="">Bez kategorii</option>
          {kategorie.map((wpis) => (
            <option key={wpis.id} value={wpis.id}>
              {wpis.name}
            </option>
          ))}
        </select>
      </div>

      {pozycje === undefined ? (
        <SzkieletPozycji />
      ) : widoczne.length === 0 ? (
        <p className="pusto">
          <strong>Brak pozycji</strong>
          {pozycje.length === 0 ? 'Dodaj pierwszy koszt przyciskiem plus.' : 'Zmień filtry.'}
        </p>
      ) : (
        <ul className="pozycje wjazd-listy">
          {widoczne.map((pozycja) => {
            const kategoriaPozycji =
              pozycja.categoryId === null ? undefined : nazwyKategorii.get(pozycja.categoryId)
            const miesiecznie = miesiecznieWZl(pozycja, kursy, dzis)
            return (
              <li key={pozycja.id} className="pozycja">
                <button
                  type="button"
                  className="pozycja__glowna"
                  onClick={() => ustawEdytowana(pozycja)}
                >
                  <span className="pozycja__opis">
                    <span className="pozycja__tytul">{pozycja.title}</span>
                    <span className="pozycja__meta">
                      {kategoriaPozycji !== undefined ? (
                        <>
                          <span
                            className="kropka-kategorii"
                            style={{ background: kategoriaPozycji.color }}
                            aria-hidden="true"
                          />
                          {kategoriaPozycji.name}
                        </>
                      ) : (
                        'Bez kategorii'
                      )}
                      {pozycja.vendorId !== null
                        ? ` · ${nazwyDostawcow.get(pozycja.vendorId) ?? ''}`
                        : ''}
                      {pozycja.type === 'income' ? ' · przychód' : ''}
                    </span>
                  </span>
                  <span className="pozycja__kwoty">
                    <span className="pozycja__kwota">
                      {waluta(pozycja.amountMinor, pozycja.currency)}
                    </span>
                    <span className="pozycja__rytm">
                      {NAZWY_RYTMU[pozycja.cadence]}
                      {miesiecznie === null ? '' : ` · ${zl(miesiecznie)}/mies.`}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="pozycja__usun"
                  onClick={() => ustawDoUsuniecia(pozycja)}
                  aria-label={`Usuń ${pozycja.title}`}
                >
                  <IkonaKosz width={17} height={17} />
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {edytowana !== null ? (
        <FormularzPozycji pozycja={edytowana} onZamknij={() => ustawEdytowana(null)} />
      ) : null}

      {doUsuniecia !== null ? (
        <Potwierdzenie
          tytul="Usunąć pozycję?"
          tresc={`„${doUsuniecia.title}” zniknie z listy. Zapłacone płatności zostaną w historii.`}
          etykietaPotwierdz="Usuń"
          niebezpieczne
          onPotwierdz={() => usun(doUsuniecia)}
          onAnuluj={() => ustawDoUsuniecia(null)}
        />
      ) : null}
    </>
  )
}
